import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { player, approachPoint, buildPath, clampPos } from "@/components/clinic/playerStore";

export function useClinicData() {
  const [departments, setDepartments] = useState([]);
  const [content, setContent] = useState([]);
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/departments").then(({ data }) => setDepartments(data));
    api.get("/content").then(({ data }) => setContent(data));
    api.get("/settings/selvix").then(({ data }) => setSettings(data));
  }, []);
  return { departments, content, settings };
}

const narrationOf = (d) => d.narration || `${d.title}. ${d.description}.`;
const END_TOUR = "Il giro della clinica è terminato. Puoi tornare in qualsiasi reparto dal menu a sinistra, oppure parlare con me per prenotare un servizio.";
const START_TOUR = "Benvenuto nel giro guidato della Clinica Virtuale Doctor Selvix. Ti accompagnerò in tutti i reparti e ti spiegherò a cosa servono. Puoi interrompere il giro in qualsiasi momento.";

function stepLabel(i, n) {
  if (i === 0) return "Prima tappa";
  if (i === n - 1) return "Ultima tappa";
  return "Prossima tappa";
}

export function useClinicGuide(narrator) {
  const { narrate } = narrator;
  const [path, setPath] = useState([]);
  const [guidePoints, setGuidePoints] = useState([]);
  const [target, setTarget] = useState(null);
  const [openDep, setOpenDep] = useState(null);
  const [tourStep, setTourStep] = useState(null);
  const tourRef = useRef(null);

  const guideTo = useCallback((dep, intro) => {
    const p = buildPath({ x: player.x, z: player.z }, approachPoint(dep));
    setTarget(dep);
    setPath(p);
    setGuidePoints([{ x: player.x, z: player.z }, ...p]);
    setOpenDep(null);
    narrate(intro || `Seguimi, ti accompagno al reparto ${dep.title}. Segui le frecce ciano sul pavimento.`);
  }, [narrate]);

  const clearPath = useCallback(() => {
    setPath([]);
    setGuidePoints([]);
    setTarget(null);
  }, []);

  const stopGuide = () => {
    clearPath();
    tourRef.current = null;
    setTourStep(null);
    narrator.stop();
  };

  const tourGo = useCallback((i) => {
    const list = tourRef.current;
    if (!list) return;
    if (i >= list.length) {
      tourRef.current = null;
      setTourStep(null);
      narrate(END_TOUR);
      return;
    }
    setTourStep({ i, n: list.length });
    guideTo(list[i], `${stepLabel(i, list.length)}: ${list[i].title}. Seguimi.`);
  }, [guideTo, narrate]);

  const startTour = (departments) => {
    tourRef.current = departments;
    setTourStep({ i: -1, n: departments.length });
    narrate(START_TOUR, () => tourGo(0));
  };

  const onArrive = useCallback(() => {
    const dep = target;
    clearPath();
    if (!dep) return;
    player.yaw = dep.rotation + Math.PI;
    if (tourRef.current) {
      const i = tourRef.current.indexOf(dep);
      narrate(narrationOf(dep), () => tourGo(i + 1));
      return;
    }
    setOpenDep(dep);
    narrate(narrationOf(dep));
  }, [target, narrate, tourGo, clearPath]);

  const walkTo = (pt) => {
    setTarget(null);
    setGuidePoints([]);
    const [x, z] = clampPos(pt.x, pt.z);
    setPath(buildPath({ x: player.x, z: player.z }, { x, z }));
  };
  const onPathStep = (t) => setPath((p) => (p[0] === t ? p.slice(1) : p));

  return { path, guidePoints, target, tourStep, openDep, setOpenDep, guideTo, stopGuide, startTour, onArrive, walkTo, onPathStep };
}
